import type {
  BucketPoint,
  DistributionPoint,
  MonthlyPoint,
  SalesCustomer,
  SalesOrder,
  SalesProduct,
  TrendPoint,
} from "../components/SalesProductAnalyticsTabsDashboard.data";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asArray(value: unknown): unknown[] {
  if (Array.isArray(value)) {
    return value;
  }
  return [];
}

function readNumber(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  }
  return fallback;
}

function readString(value: unknown, fallback = ""): string {
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return fallback;
}

function readOptionalString(value: unknown, fallback = "Not Available"): string {
  const text = readString(value, "").trim();
  return text && text !== "-" && text !== "—" && text !== "â€”" ? text : fallback;
}

function isUnavailableDisplayValue(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    (typeof value === "string" && (!value.trim() || value.trim() === "-"))
  );
}

function readStringList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map(item => readString(item, "").trim())
      .filter(Boolean);
  }
  const raw = readString(value, "").trim();
  if (!raw) {
    return [];
  }
  return raw.split(",").map(item => item.trim()).filter(Boolean);
}

function formatDateLikeDisplay(value: string): string {
  const trimmed = value.trim();
  return /^\d{4}-\d{2}-\d{2}/.test(trimmed) ? trimmed.slice(0, 10) : trimmed;
}

function startCaseFromKey(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }
  return trimmed
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

/** Backend sends channel: null for orders with no referrer — show it as "None", not a generic label. */
function normalizeChannelLabel(value: unknown): string {
  const raw = readString(value, "").trim();
  if (!raw) {
    return "None";
  }
  const normalized = raw.toLowerCase();
  if (normalized === "web" || normalized === "online_store") {
    return "Web";
  }
  if (normalized === "shopify_draft_order") {
    return "Shopify Draft Order";
  }
  return startCaseFromKey(raw);
}

function normalizeCustomerSegmentLabel(value: unknown, fallback = "—"): string {
  const raw = readString(value, "").trim();
  const normalized = raw.toLowerCase().replace(/[_-]+/g, " ");
  if (normalized.includes("return") || normalized.includes("repeat")) {
    return "Returning";
  }
  if (normalized.includes("new") || normalized.includes("first")) {
    return "New";
  }
  return raw || fallback;
}

export type SalesProductOverviewMetricCard = {
  label: string;
  value: string;
  changePercent?: number;
  changePercentFormattedValue?: string;
  comparisonLabel?: string;
  statusHint?: string;
  tone?: "positive" | "negative" | "neutral";
};

export type SalesProductOverviewTopProduct = {
  id: string;
  name: string;
  orders: number;
  ordersFormattedValue?: string;
  revenue: number;
  revenueFormattedValue?: string;
  productType: string;
  imageUrl?: string;
};

export type SalesProductSalesCardsView = {
  totalSales: number;
  totalRevenue: number;
  /** Pre-formatted amount from the API (e.g. "USD 8,308,501"), preferred over formatting `totalRevenue` locally. */
  totalRevenueFormattedValue?: string;
  topChannel: {
    channel: string;
    label: string;
    revenue: number;
    revenueFormattedValue?: string;
  } | null;
  totalOrders: number;
  /** Pre-formatted count from the API, preferred over formatting `totalOrders` locally. */
  totalOrdersFormattedValue?: string;
  orderStatusBreakdown: Array<{
    status: string;
    count: number;
    countFormattedValue?: string;
    percent: number;
    percentFormattedValue?: string;
    label: string;
  }>;
  notes?: string[];
};

export type SalesProductOrderDetailLineItem = {
  id: string;
  productTitle: string;
  variantTitle: string;
  imageUrl: string;
  price: number;
  priceFormattedValue?: string;
  quantity: number;
};

export type SalesProductOrderDetailView = {
  id: string;
  orderName: string;
  orderDateTime: string;
  orderDateTimeFormattedValue?: string;
  financialStatus: string;
  fulfillmentStatus: string;
  subtotal: number;
  subtotalFormattedValue?: string;
  shipping: number;
  shippingFormattedValue?: string;
  total: number;
  totalFormattedValue?: string;
  customer: {
    name: string;
    phone: string;
    email: string;
  };
  lineItems: SalesProductOrderDetailLineItem[];
};

export type SalesProductOverviewView = {
  currencyLabel: string;
  metricCards: SalesProductOverviewMetricCard[];
  trend: TrendPoint[];
  channels: DistributionPoint[];
  channelSharesPct: number[];
  topProducts: SalesProductOverviewTopProduct[];
  topProductsByOrders: SalesProductOverviewTopProduct[];
  topProductsByRevenue: SalesProductOverviewTopProduct[];
  segmentation: DistributionPoint[] | null;
  financialSummary: {
    revenue: number;
    grossProfit: number;
    taxes: number;
    expenses: number;
    refunds: number;
    netProfit: number;
  } | null;
  /** Backend footnotes (e.g. data caveats). */
  notes?: string[];
};

function unwrapPayload(raw: unknown): Record<string, unknown> {
  if (Array.isArray(raw)) {
    return { items: raw };
  }
  if (!isRecord(raw)) {
    return {};
  }
  const data = raw.data;
  if (isRecord(data)) {
    return data;
  }
  return raw;
}

function normalizeTone(
  delta: number | null | undefined,
): "positive" | "negative" | "neutral" | undefined {
  if (delta === null || delta === undefined || Number.isNaN(delta)) {
    return undefined;
  }
  if (delta > 0) {
    return "positive";
  }
  if (delta < 0) {
    return "negative";
  }
  return "neutral";
}

function normalizeOverviewKpiRow(row: unknown): SalesProductOverviewMetricCard | null {
  if (!isRecord(row)) {
    return null;
  }
  const label = readString(row.title ?? row.label ?? row.name ?? row.key, "");
  const value = readString(row.value ?? row.display_value ?? row.formatted_value, "");
  if (!label && !value) {
    return null;
  }
  const delta = row.delta;
  const changePercent =
    typeof delta === "number"
      ? delta
      : readNumber(delta, Number.NaN);
  const comparisonLabel = readString(
    row.comparison_label ?? row.delta_label ?? "vs prior period",
    "vs prior period",
  );
  const statusHint = row.status_hint ?? row.hint;
  return {
    label: label || "—",
    value: value || "—",
    changePercent: Number.isFinite(changePercent) ? changePercent : undefined,
    comparisonLabel,
    statusHint: typeof statusHint === "string" ? statusHint : undefined,
    tone: normalizeTone(
      typeof delta === "number"
        ? delta
        : readNumber(delta, Number.NaN),
    ),
  };
}

function normalizeDailyRevenue(rows: unknown): TrendPoint[] {
  return asArray(rows).map((row, index) => {
    if (!isRecord(row)) {
      return { date: String(index), revenue: 0, orders: 0 };
    }
    const rawDate =
      readString(
        row.date ?? row.day ?? row.label ?? row.bucket ?? row.period,
        String(index),
      ) || String(index);
    const date = rawDate.trim();
    const revenue = readNumber(
      row.total_sales ?? row.revenue ?? row.total_revenue ?? row.amount ?? row.value,
      0,
    );
    const orders = readNumber(row.orders ?? row.order_count, 0);
    return { date, revenue, orders };
  });
}

function normalizeChannelMix(rows: unknown): { channels: DistributionPoint[]; sharesPct: number[] } {
  const list = asArray(rows);
  const channels: DistributionPoint[] = [];
  const sharesPct: number[] = [];
  let valueTotal = 0;
  for (const row of list) {
    if (!isRecord(row)) {
      continue;
    }
    const label = readString(
      row.channel_name ??
      row.order_source_name ??
      row.channel ??
      row.sales_channel ??
      row.source_channel ??
      row.label ??
      row.name ??
      row.title,
      "",
    );
    const value = readNumber(
      row.total_sales ??
      row.revenue ??
      row.net_revenue ??
      row.amount ??
      row.total_revenue ??
      row.value,
      0,
    );
    const formattedValue = readString(
      row.total_salesFormattedValue ??
      row.totalSalesFormattedValue ??
      row.revenueFormattedValue ??
      row.amountFormattedValue ??
      row.formattedValue,
      "",
    ).trim();
    const share = readNumber(row.share_pct ?? row.share_percent ?? row.percent, Number.NaN);
    const sharePercent = Number.isFinite(share) ? Math.min(100, Math.max(0, share)) : Number.NaN;
    channels.push({
      label: normalizeChannelLabel(label),
      value,
      ...(Number.isFinite(sharePercent) ? { sharePercent } : {}),
      ...(formattedValue ? { formattedValue } : {}),
    });
    sharesPct.push(sharePercent);
    valueTotal += value;
  }
  const resolvedShares = sharesPct.map((p, i) =>
    Number.isFinite(p)
      ? p
      : valueTotal > 0 && channels[i]
        ? Math.round((channels[i].value / valueTotal) * 1000) / 10
        : 0
  );
  return { channels, sharesPct: resolvedShares };
}

function normalizeTopProducts(rows: unknown): SalesProductOverviewView["topProducts"] {
  return asArray(rows)
    .map((row, index) => {
      if (!isRecord(row)) {
        return null;
      }
      const id = readString(row.id ?? row.product_id ?? row.variant_id ?? `p-${index}`, `p-${index}`);
      const name = readString(
        row.product_title ??
        row.product ??
        row.title ??
        row.name ??
        row.product_name,
        "—",
      );
      const orders = readNumber(row.orders ?? row.order_count, 0);
      const ordersFormattedValue = readString(row.ordersFormattedValue, "").trim() || undefined;
      const revenue = readNumber(
        row.total_sales ??
        row.line_item_total_price ??
        row.revenue ??
        row.total_revenue ??
        row.net_revenue ??
        row.sales ??
        row.amount,
        0,
      );
      const revenueFormattedValue = readString(
        row.lineItemTotalPriceFormattedValue ??
        row.total_salesFormattedValue ??
        row.revenueFormattedValue,
        "",
      ).trim() || undefined;
      const productType = readString(row.product_type ?? row.type ?? row.category, "");
      const imageUrl = readString(
        row.image_url ??
        row.imageUrl ??
        row.product_image_url ??
        row.product_image ??
        row.image ??
        row.thumbnail_url ??
        row.thumbnail,
        "",
      );
      return {
        id,
        name,
        orders,
        ...(ordersFormattedValue ? { ordersFormattedValue } : {}),
        revenue,
        ...(revenueFormattedValue ? { revenueFormattedValue } : {}),
        productType,
        ...(imageUrl ? { imageUrl } : {}),
      };
    })
    .filter(Boolean) as SalesProductOverviewView["topProducts"];
}

function normalizeSegmentation(rows: unknown): DistributionPoint[] | null {
  const list = asArray(rows);
  if (!list.length) {
    return null;
  }
  const out: DistributionPoint[] = [];
  for (const row of list) {
    if (!isRecord(row)) {
      continue;
    }
    out.push({
      label: normalizeCustomerSegmentLabel(
        row.label ?? row.segment ?? row.name ?? row.customer_type ?? row.type,
      ),
      value: readNumber(row.value ?? row.count ?? row.customers, 0),
    });
  }
  return out.length ? out : null;
}

function normalizeFinancialSummaryBlock(row: unknown): SalesProductOverviewView["financialSummary"] {
  if (!isRecord(row)) {
    return null;
  }
  const revenue = readNumber(row.revenue ?? row.total_revenue, 0);
  const grossProfit = readNumber(row.gross_profit ?? row.grossProfit, 0);
  const taxes = readNumber(row.taxes ?? row.tax, 0);
  const expenses = readNumber(row.expenses ?? row.operating_expenses, 0);
  const refunds = readNumber(row.refunds ?? row.refund_total, 0);
  const netProfit = readNumber(
    row.net_profit ?? row.netProfit,
    revenue - grossProfit - taxes - expenses - refunds,
  );
  if (!revenue && !grossProfit && !taxes && !expenses && !refunds && !netProfit) {
    return null;
  }
  return { revenue, grossProfit, taxes, expenses, refunds, netProfit };
}

/** `{ value, formattedValue, previous_value, percent_change, percent_changeFormattedValue }` blocks used by `/sales-product/overview`. */
function readMetricSeries(
  row: unknown,
): { value: number; pct: number | null; formattedValue?: string; pctFormattedValue?: string } | null {
  if (!isRecord(row) || !("value" in row)) {
    return null;
  }
  const value = readNumber(row.value, Number.NaN);
  if (!Number.isFinite(value)) {
    return null;
  }
  const pctRaw = row.percent_change;
  const pct =
    pctRaw === null || pctRaw === undefined
      ? null
      : readNumber(pctRaw, Number.NaN);
  const formattedValue = readString(row.formattedValue, "").trim() || undefined;
  const pctFormattedValue = readString(row.percent_changeFormattedValue, "").trim() || undefined;
  return {
    value,
    pct: pct !== null && Number.isFinite(pct) ? pct : null,
    ...(formattedValue ? { formattedValue } : {}),
    ...(pctFormattedValue ? { pctFormattedValue } : {}),
  };
}

function readMetricSeriesFromKeys(
  root: Record<string, unknown>,
  keys: readonly string[],
): { value: number; pct: number | null; formattedValue?: string; pctFormattedValue?: string } | null {
  for (const key of keys) {
    const series = readMetricSeries(root[key]);
    if (series) {
      return series;
    }
  }
  return null;
}

function formatMoneyForKpi(n: number, currency: string): string {
  if (!Number.isFinite(n)) {
    return `${currency} 0`;
  }
  const abs = Math.abs(n);
  if (abs >= 1e9) {
    return `${currency} ${(n / 1e9).toFixed(2)}B`;
  }
  if (abs >= 1e6) {
    return `${currency} ${(n / 1e6).toFixed(2)}M`;
  }
  if (abs >= 1e3) {
    return `${currency} ${(n / 1e3).toFixed(1)}k`;
  }
  return `${currency} ${Math.round(n).toLocaleString()}`;
}

function formatShortDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}-${month}-${d.getFullYear()}`;
}

function comparisonLabelFromOverviewRoot(root: Record<string, unknown>): string {
  const cp = root.comparison_period;
  if (!isRecord(cp)) {
    return "vs comparison period";
  }
  const a = readString(cp.start_date, "");
  const b = readString(cp.end_date, "");
  if (a && b) {
    return `vs ${formatShortDate(a)} → ${formatShortDate(b)}`;
  }
  return "vs comparison period";
}

/**
 * Maps the live `/shopify/sales-product/overview` payload (nested metrics + scalars).
 */
function buildMetricCardsFromShopifyOverviewApi(root: Record<string, unknown>): SalesProductOverviewMetricCard[] {
  const currency = readString(root.currency ?? root.currency_code ?? root.currency_symbol, "PKR");
  const cmp = comparisonLabelFromOverviewRoot(root);
  const cards: SalesProductOverviewMetricCard[] = [];

  const totalSales = readMetricSeriesFromKeys(root, ["total_sales", "net_revenue"]);
  if (totalSales) {
    cards.push({
      label: "Total Sales",
      value: totalSales.formattedValue ?? formatMoneyForKpi(totalSales.value, currency),
      changePercent: totalSales.pct !== null ? totalSales.pct : undefined,
      changePercentFormattedValue: totalSales.pctFormattedValue,
      comparisonLabel: cmp,
      tone: normalizeTone(totalSales.pct ?? undefined),
    });
  }

  const refunds = readMetricSeries(root.refunds);
  if (refunds) {
    cards.push({
      label: "Refunds",
      value: refunds.formattedValue ?? formatMoneyForKpi(refunds.value, currency),
      changePercent: refunds.pct !== null ? refunds.pct : undefined,
      changePercentFormattedValue: refunds.pctFormattedValue,
      comparisonLabel: cmp,
      tone: refunds.pct !== null ? normalizeTone(-refunds.pct) : undefined,
    });
  }

  const ord = readMetricSeries(root.orders);
  if (ord) {
    cards.push({
      label: "Orders",
      value: ord.formattedValue ?? String(Math.round(ord.value)),
      changePercent: ord.pct !== null ? ord.pct : undefined,
      changePercentFormattedValue: ord.pctFormattedValue,
      comparisonLabel: cmp,
      tone: normalizeTone(ord.pct ?? undefined),
    });
  }

  const aov = readMetricSeries(root.avg_order_value);
  if (aov) {
    cards.push({
      label: "Avg. Order Value",
      value: aov.formattedValue ?? formatMoneyForKpi(aov.value, currency),
      changePercent: aov.pct !== null ? aov.pct : undefined,
      changePercentFormattedValue: aov.pctFormattedValue,
      comparisonLabel: cmp,
      tone: normalizeTone(aov.pct ?? undefined),
    });
  }

  /** Set true to show “Inventory value” on overview again (also unhide in `SalesProductAnalyticsTabsDashboard` filter if needed). */
  const includeInventoryValueKpi = false;
  if (includeInventoryValueKpi && "inventory_value" in root) {
    const invVal = readNumber(root.inventory_value, 0);
    const low = readNumber(root.low_stock_count, 0);
    cards.push({
      label: "Inventory value",
      value: `${Math.round(invVal).toLocaleString()} units`,
      statusHint: low ? `${Math.round(low).toLocaleString()} low-stock products` : undefined,
      tone: "neutral",
    });
  }

  const custOrdered = readMetricSeries(root.total_customers_ordered);
  if (custOrdered) {
    cards.push({
      label: "Customers Ordered",
      value: custOrdered.formattedValue ?? String(Math.round(custOrdered.value)),
      changePercent: custOrdered.pct !== null ? custOrdered.pct : undefined,
      changePercentFormattedValue: custOrdered.pctFormattedValue,
      comparisonLabel: cmp,
      tone: normalizeTone(custOrdered.pct ?? undefined),
    });
  } else if ("total_customers_ordered" in root) {
    cards.push({
      label: "Customers Ordered",
      value: String(Math.round(readNumber(root.total_customers_ordered, 0))),
      comparisonLabel: cmp,
      tone: "neutral",
    });
  }

  const newCustWeek = readMetricSeries(root.new_customers_this_week);
  if (newCustWeek) {
    cards.push({
      label: "New customers this week",
      value: newCustWeek.formattedValue ?? String(Math.round(newCustWeek.value)),
      changePercent: newCustWeek.pct !== null ? newCustWeek.pct : undefined,
      changePercentFormattedValue: newCustWeek.pctFormattedValue,
      comparisonLabel: "vs prior week",
      tone: normalizeTone(newCustWeek.pct ?? undefined),
    });
  }

  return cards;
}

export function normalizeSalesProductOverviewResponse(
  raw: unknown,
  currencyFallback = "PKR",
): SalesProductOverviewView {
  const root = unwrapPayload(raw);
  const currencyLabel = readString(
    root.currency ?? root.currency_code ?? root.currency_symbol,
    currencyFallback,
  );

  const kpiSource =
    root.kpi_cards ??
    root.kpis ??
    root.overview_kpis ??
    root.cards ??
    root.metrics;
  const metricCards: SalesProductOverviewMetricCard[] = [];
  for (const row of asArray(kpiSource)) {
    const card = normalizeOverviewKpiRow(row);
    if (card) {
      metricCards.push(card);
    }
  }
  if (!metricCards.length) {
    const fromShopifyShape = buildMetricCardsFromShopifyOverviewApi(root);
    metricCards.push(...fromShopifyShape);
  }

  const daily =
    root.daily_total_sales ??
    root.daily_revenue ??
    root.revenue_daily ??
    root.daily_sales ??
    root.sales_trend;
  const trend = normalizeDailyRevenue(daily);

  const mixSource =
    root.channel_mix ?? root.channels ?? root.sales_by_channel ?? root.channel_distribution;
  const { channels, sharesPct } = normalizeChannelMix(mixSource);

  const topOrdersSource =
    root.top_10_products_by_orders ??
    root.top_products_by_orders ??
    root.top_products_orders ??
    root.products_by_orders ??
    root.top_order_products ??
    root.top_products_by_order_count;
  const topRevenueSource =
    root.top_10_products_by_total_sales ??
    root.top_10_products_by_line_item_total_price ??
    root.top_products_by_revenue ??
    root.top_products_revenue ??
    root.products_by_revenue ??
    root.top_revenue_products ??
    root.top_products_by_sales ??
    root.top_products_sales;
  const topSource =
    root.top_products ??
    root.top_products_by_stock ??
    root.products_top_stock ??
    root.top_stock_products;
  const topProducts = normalizeTopProducts(topSource);
  const topProductsByOrders = normalizeTopProducts(topOrdersSource);
  const topProductsByRevenue = normalizeTopProducts(topRevenueSource);

  const segmentation =
    normalizeSegmentation(
      root.customer_segmentation ?? root.customers_segmentation ?? root.segmentation,
    ) ?? null;

  const financialSummary = normalizeFinancialSummaryBlock(
    root.financial_summary ?? root.financials ?? root.pnl_summary,
  );

  const notesRaw = asArray(root.notes);
  const notes = notesRaw
    .map(n => readString(n, ""))
    .filter(s => s.length > 0);

  return {
    currencyLabel,
    metricCards,
    trend,
    channels,
    channelSharesPct: sharesPct,
    topProducts,
    topProductsByOrders: topProductsByOrders.length ? topProductsByOrders : topProducts,
    topProductsByRevenue: topProductsByRevenue.length ? topProductsByRevenue : topProducts,
    segmentation,
    financialSummary,
    notes: notes.length ? notes : undefined,
  };
}

export function normalizeSalesByChannelForChart(
  raw: unknown,
  metric: "sales" | "orders" = "sales",
): DistributionPoint[] {
  if (Array.isArray(raw)) {
    return normalizeChannelRows(raw, metric);
  }
  const root = unwrapPayload(raw);
  const mixSource =
    root.rows ??
    root.channels ??
    root.channel_mix ??
    root.items ??
    root.data ??
    [];
  return normalizeChannelRows(mixSource, metric);
}

function normalizeChannelRows(rows: unknown, metric: "sales" | "orders"): DistributionPoint[] {
  if (metric === "sales") {
    return normalizeChannelMix(rows).channels;
  }
  return asArray(rows).map((row, index) => {
    if (!isRecord(row)) {
      return { label: `Channel ${index + 1}`, value: 0 };
    }
    const label = readString(
      row.channel_name ??
      row.order_source_name ??
      row.channel ??
      row.sales_channel ??
      row.source_channel ??
      row.label ??
      row.name ??
      row.title,
      "",
    );
    const value = readNumber(
      row.orders ??
      row.total_orders ??
      row.order_count ??
      row.count ??
      row.value,
      0,
    );
    return { label: normalizeChannelLabel(label), value };
  });
}

export function normalizeSalesCardsResponse(raw: unknown): SalesProductSalesCardsView {
  const payload = unwrapPayload(raw);
  const topChannelRecord = isRecord(payload.top_channel) ? payload.top_channel : null;
  const breakdown = asArray(payload.order_status_breakdown).map(row => {
    if (!isRecord(row)) {
      return { status: "", count: 0, percent: 0, label: "" };
    }
    const label = startCaseFromKey(readString(row.label ?? row.status ?? row.name, ""));
    return {
      status: readString(row.status ?? row.name, ""),
      count: readNumber(row.count ?? row.orders ?? row.total_orders, 0),
      countFormattedValue: readString(row.countFormattedValue, "").trim() || undefined,
      percent: readNumber(row.percent ?? row.percentage ?? row.share_percent, 0),
      percentFormattedValue: readString(row.percentFormattedValue, "").trim() || undefined,
      label,
    };

  }).filter(row => row.label.trim().length > 0 || row.count > 0);
  const notes = asArray(payload.notes)
    .map(note => readString(note, ""))
    .filter(note => note.length > 0);
  const totalRevenueFormattedValue = readString(
    payload.totalSalesFormattedValue ??
    payload.total_salesFormattedValue ??
    payload.totalRevenueFormattedValue ??
    payload.total_revenueFormattedValue,
    "",
  ).trim();
  const totalOrdersFormattedValue = readString(
    payload.total_ordersFormattedValue ?? payload.ordersFormattedValue ?? payload.order_countFormattedValue,
    "",
  ).trim();

  return {
    totalSales: readNumber(payload.total_sales ?? payload.total_revenue ?? payload.revenue, 0),
    totalRevenue: readNumber(
      payload.total_sales ??
      payload.total_revenue ??
      payload.revenue ??
      payload.net_revenue,
      0,
    ),
    ...(totalRevenueFormattedValue ? { totalRevenueFormattedValue } : {}),
    topChannel: topChannelRecord
      ? {
        channel: readString(topChannelRecord.channel ?? topChannelRecord.key, ""),
        label: normalizeChannelLabel(
          readString(
            topChannelRecord.label ?? topChannelRecord.channel ?? topChannelRecord.name,
            "",
          ),
        ),
        revenue: readNumber(
          topChannelRecord.revenue ??
          topChannelRecord.total_sales ??
          topChannelRecord.value,
          0,
        ),
        revenueFormattedValue: readString(
          topChannelRecord.revenueFormattedValue ?? topChannelRecord.total_salesFormattedValue,
          "",
        ).trim() || undefined,
      }
      : null,
    totalOrders: readNumber(payload.total_orders ?? payload.orders ?? payload.order_count, 0),
    ...(totalOrdersFormattedValue ? { totalOrdersFormattedValue } : {}),
    orderStatusBreakdown: breakdown,
    notes: notes.length ? notes : undefined,
  };
}

export function normalizeSalesOrdersResponse(raw: unknown): {
  orders: SalesOrder[];
  total: number;
  page: number;
  pageSize: number;
} {
  const root = isRecord(raw) ? raw : {};
  const payload = unwrapPayload(raw);
  const rows = asArray(
    payload.items ??
    payload.orders ??
    payload.results ??
    payload.rows ??
    root.items ??
    root.orders ??
    root.rows,
  );

  const meta = isRecord(payload.meta)
    ? payload.meta
    : isRecord(root.meta)
      ? root.meta
      : null;

  const orders: SalesOrder[] = rows.map((row, index) => {
    if (!isRecord(row)) {
      return {
        id: `ord-${index}`,
        orderName: "Not available",
        customerFullName: "—",
        totalPrice: Number.NaN,
        orderStatus: "Not available",
        date: "Not available",
      };
    }
    const id = readString(row.id ?? row.order_id ?? `ord-${index}`, `ord-${index}`);
    const orderName = readString(
      row.order_name ?? row.name ?? row.order_number ?? row.number,
      "Not available",
    );
    const customerRecord = isRecord(row.customer) ? row.customer : null;
    const customerFullName = readString(
      row.customer_full_name ?? row.customer_name ?? customerRecord?.name ?? row.buyer_name,
      "—",
    );
    const totalPrice = readNumber(row.total_price ?? row.total ?? row.amount, Number.NaN);
    const totalPriceFormattedValue = readString(
      row.totalPriceFormattedValue ?? row.total_priceFormattedValue,
      "",
    ).trim() || undefined;
    const orderStatus = readString(
      row.order_status ?? row.status ?? row.fulfillment_status ?? row.financial_status,
      "Not available",
    );
    const orderStatusLabel = readString(
      row.order_status_label ?? row.status_label,
      "",
    ).trim() || undefined;
    const rawDate = readString(row.date ?? row.created_at ?? row.processed_at, "");
    const date = rawDate ? rawDate.slice(0, 10) : "Not available";
    const dateFormattedValue = readString(row.dateFormattedValue, "").trim() || undefined;

    return {
      id,
      orderName,
      customerFullName,
      totalPrice,
      totalPriceFormattedValue,
      orderStatus,
      orderStatusLabel,
      date,
      dateFormattedValue,
    };
  });

  const total = readNumber(
    meta?.total_items ??
    meta?.total ??
    meta?.total_count ??
    payload.total ??
    payload.total_count ??
    payload.count ??
    root.total ??
    orders.length,
    orders.length,
  );
  const page = readNumber(meta?.page ?? payload.page ?? root.page, 1);
  const pageSize = readNumber(
    meta?.page_size ?? payload.page_size ?? payload.per_page ?? root.page_size,
    10,
  );

  return { orders, total, page, pageSize };
}

export function normalizeSalesOrderDetailResponse(raw: unknown): SalesProductOrderDetailView {
  const root = isRecord(raw) ? raw : {};
  const payload = unwrapPayload(raw);
  const order = isRecord(payload.order)
    ? payload.order
    : isRecord(payload.order_detail)
      ? payload.order_detail
      : isRecord(payload.detail)
        ? payload.detail
        : payload;
  const customerRecord = isRecord(order.customer) ? order.customer : {};
  const shippingRecord = isRecord(order.shipping) ? order.shipping : {};
  const totalsRecord = isRecord(order.totals)
    ? order.totals
    : isRecord(order.financials)
      ? order.financials
      : {};
  const rows = asArray(
    order.line_items ??
    order.items ??
    order.products ??
    payload.line_items ??
    payload.items ??
    root.line_items,
  );
  const lineItems = rows.map((item, index): SalesProductOrderDetailLineItem => {
    const row = isRecord(item) ? item : {};
    const imageRecord = isRecord(row.image) ? row.image : {};
    const variantRecord = isRecord(row.variant) ? row.variant : {};
    return {
      id: readString(row.id ?? row.line_item_id ?? row.product_id ?? `item-${index}`, `item-${index}`),
      productTitle: readString(row.product_title ?? row.title ?? row.name, "Product"),
      variantTitle: readString(
        row.variant_title ?? variantRecord.title ?? row.variant_name ?? row.sku,
        "",
      ),
      imageUrl: readString(
        row.image_url ??
        row.product_image_url ??
        row.product_image ??
        row.picture ??
        row.image_src ??
        imageRecord.src ??
        imageRecord.url,
        "",
      ),
      price: readNumber(row.price ?? row.unit_price ?? row.amount, 0),
      priceFormattedValue: readString(
        row.priceFormattedValue ?? row.price_formatted_value,
        "",
      ) || undefined,
      quantity: readNumber(row.quantity ?? row.qty, 0),
    };
  });
  const subtotal = readNumber(
    order.subtotal_price ?? order.subtotal ?? totalsRecord.subtotal ?? totalsRecord.subtotal_price,
    lineItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
  );
  const shipping = readNumber(
    order.shipping_price ??
    order.shipping_total ??
    order.total_shipping_price_set ??
    shippingRecord.price ??
    totalsRecord.shipping,
    0,
  );
  const total = readNumber(
    order.total_price ?? order.total ?? order.order_total ?? totalsRecord.total ?? totalsRecord.total_price,
    subtotal + shipping,
  );
  const subtotalFormattedValue = readString(
    totalsRecord.subtotalFormattedValue ?? order.subtotalFormattedValue,
    "",
  ).trim() || undefined;
  const shippingFormattedValue = readString(
    totalsRecord.shippingFormattedValue ?? order.shippingFormattedValue,
    "",
  ).trim() || undefined;
  const totalFormattedValue = readString(
    totalsRecord.totalFormattedValue ?? order.totalFormattedValue,
    "",
  ).trim() || undefined;
  const firstName = readString(customerRecord.first_name ?? order.customer_first_name, "");
  const lastName = readString(customerRecord.last_name ?? order.customer_last_name, "");
  const customerName = readString(
    order.customer_full_name ?? order.customer_name ?? customerRecord.name,
    `${firstName} ${lastName}`.trim() || "Customer",
  );

  return {
    id: readString(order.id ?? order.order_id ?? payload.order_id, ""),
    orderName: readString(order.order_name ?? order.name ?? order.order_number ?? order.number, "Order"),
    orderDateTime: readString(
      order.order_created_at ??
      order.created_at ??
      order.processed_at ??
      order.order_datetime ??
      order.date_time ??
      (
        order.order_date && order.order_time
          ? `${readString(order.order_date)} ${readString(order.order_time)}`
          : order.order_date
      ) ??
      order.date,
      "",
    ),
    orderDateTimeFormattedValue: readString(
      order.order_created_atFormattedValue ??
      order.created_atFormattedValue ??
      order.processed_atFormattedValue ??
      order.order_datetimeFormattedValue ??
      order.date_timeFormattedValue,
      "",
    ).trim() || undefined,
    financialStatus: readString(
      order.financial_status ??
      order.payment_status ??
      totalsRecord.order_financial_status ??
      totalsRecord.financial_status ??
      order.status,
      "",
    ),
    fulfillmentStatus: readString(order.fulfillment_status ?? order.order_status, ""),
    subtotal,
    subtotalFormattedValue,
    shipping,
    shippingFormattedValue,
    total,
    totalFormattedValue,
    customer: {
      name: customerName,
      phone: readString(order.customer_phone ?? customerRecord.phone ?? customerRecord.default_phone, "—"),
      email: readString(order.customer_email ?? customerRecord.email, "—"),
    },
    lineItems,
  };
}

function normalizeInventoryProductStatus(raw: unknown): SalesProduct["status"] | null {
  if (isUnavailableDisplayValue(raw)) {
    return null;
  }
  const s = readString(raw, "").toLowerCase().replace(/[\s-]+/g, "_");
  if (s.includes("out")) {
    return "out_of_stock";
  }
  if (s.includes("low")) {
    return "low_stock";
  }
  if (s.includes("in") || s.includes("available")) {
    return "in_stock";
  }
  return null;
}

export function normalizeInventoryByProductResponse(raw: unknown): SalesProduct[] {
  const payload = unwrapPayload(raw);
  const rows = asArray(
    payload.items ?? payload.products ?? payload.results ?? payload.rows,
  );

  return rows.map((row, index) => {
    if (!isRecord(row)) {
      return {
        id: `prod-${index}`,
        name: "—",
        sku: "",
        category: "",
        price: 0,
        cost: 0,
        stock: 0,
        reorderPoint: 0,
        status: "in_stock",
        statusUnavailable: true,
      };
    }
    const id = readString(row.id ?? row.product_id ?? `prod-${index}`, `prod-${index}`);
    const name = readString(
      row.product ?? row.title ?? row.name ?? row.product_name,
      "—",
    );
    const sku = readString(row.sku ?? row.variant_sku, "");
    const category = readString(row.category ?? row.product_type, "");
    const price = readNumber(row.price ?? row.unit_price, 0);
    const cost = readNumber(row.cost ?? row.unit_cost, 0);
    const stockRaw = row.stock ?? row.inventory_quantity ?? row.quantity_on_hand;
    const stockUnavailable = isUnavailableDisplayValue(stockRaw);
    const stock = stockUnavailable ? 0 : readNumber(stockRaw, 0);
    const reorderPoint = readNumber(row.reorder_point ?? row.reorder_level, 0);
    const statusRaw = row.status ?? row.inventory_status;
    const normalizedStatus = normalizeInventoryProductStatus(statusRaw);
    const imageUrl = readString(
      row.product_image_url ??
        row.image_url ??
        row.imageUrl ??
        row.product_image ??
        row.image ??
        row.thumbnail_url ??
        row.thumbnail,
      "",
    );

    return {
      id,
      name,
      sku,
      category,
      price,
      cost,
      stock,
      ...(stockUnavailable ? { stockUnavailable } : {}),
      reorderPoint,
      status: normalizedStatus ?? "in_stock",
      ...(normalizedStatus ? {} : { statusUnavailable: true }),
      ...(imageUrl ? { imageUrl } : {}),
    };
  });
}

export type InventorySummaryMetrics = {
  totalProducts: number;
  totalProductsFormattedValue?: string;
  inStock: number;
  inStockFormattedValue?: string;
  lowStock: number;
  lowStockFormattedValue?: string;
  outOfStock: number;
  inventoryValue: number;
  inventoryValueFormattedValue?: string;
  lowStockThreshold?: number;
  lowStockThresholdFormattedValue?: string;
};

export function normalizeInventorySummaryResponse(raw: unknown): InventorySummaryMetrics {
  const payload = unwrapPayload(raw);
  const totalProducts = readNumber(
    payload.total_products ?? payload.product_count ?? payload.skus ?? payload.total_skus,
    0,
  );
  const inStock = readNumber(
    payload.in_stock_products ?? payload.in_stock ?? payload.in_stock_count,
    0,
  );
  const lowStock = readNumber(
    payload.low_stock_products ?? payload.low_stock ?? payload.low_stock_count,
    0,
  );
  const outOfStock = readNumber(
    payload.out_of_stock_products ?? payload.out_of_stock ?? payload.out_of_stock_count,
    Math.max(0, totalProducts - inStock - lowStock),
  );
  const inventoryValue = readNumber(
    payload.inventory_value ?? payload.stock_value ?? payload.total_value,
    0,
  );
  const lowStockThreshold = readNumber(
    payload.low_stock_threshold ?? payload.low_stock_threshold_value,
    Number.NaN,
  );
  const fmt = (key: string) => readString(payload[key], "").trim() || undefined;
  return {
    totalProducts,
    totalProductsFormattedValue: fmt("total_productsFormattedValue"),
    inStock,
    inStockFormattedValue: fmt("in_stock_productsFormattedValue"),
    lowStock,
    lowStockFormattedValue: fmt("low_stock_productsFormattedValue"),
    outOfStock,
    inventoryValue,
    inventoryValueFormattedValue: fmt("inventory_valueFormattedValue"),
    ...(Number.isFinite(lowStockThreshold) ? { lowStockThreshold } : {}),
    lowStockThresholdFormattedValue: fmt("low_stock_thresholdFormattedValue"),
  };
}

export function normalizeInventoryCategoriesResponse(raw: unknown): string[] {
  const root = isRecord(raw) ? raw : {};
  const payload = unwrapPayload(raw);
  const rows = asArray(
    (Array.isArray(raw) ? raw : root.data) ??
      payload.categories ??
      payload.product_types ??
      payload.product_type ??
      payload.items ??
      payload.results ??
      payload.rows,
  );
  const seen = new Set<string>();

  return rows.reduce<string[]>((categories, row) => {
    const category = isRecord(row)
      ? readString(row.category ?? row.product_type ?? row.name ?? row.value ?? row.label, "")
      : readString(row, "");
    const trimmed = category.trim();
    const key = trimmed.toLowerCase();
    if (trimmed && !seen.has(key)) {
      seen.add(key);
      categories.push(trimmed);
    }
    return categories;
  }, []);
}

/** Maps sales summary API (if present) into monthly / financial helpers — best-effort. */
export function normalizeSalesSummaryToMonthly(raw: unknown): MonthlyPoint[] {
  const payload = unwrapPayload(raw);
  const series = asArray(payload.monthly ?? payload.monthly_pl ?? payload.series);
  if (!series.length) {
    return [];
  }
  return series.map((row, index) => {
    if (!isRecord(row)) {
      return { month: String(index), revenue: 0, expenses: 0, profit: 0 };
    }
    return {
      month: readString(row.month ?? row.label ?? row.period, String(index)),
      revenue: readNumber(row.revenue, 0),
      expenses: readNumber(row.expenses ?? row.costs, 0),
      profit: readNumber(row.profit ?? row.net_profit, 0),
    };
  });
}

/** Scalar or `{ value }` money fields from sales / financials summary APIs. */
function readMoneyScalar(payload: Record<string, unknown>, key: string): number {
  const v = payload[key];
  if (v === undefined) {
    return Number.NaN;
  }
  if (isRecord(v) && "value" in v) {
    return readNumber(v.value, Number.NaN);
  }
  return readNumber(v, Number.NaN);
}

export function normalizeSalesSummaryToFinancial(raw: unknown): {
  revenue: number;
  cogs: number;
  grossProfit: number;
  taxes: number;
  expenses: number;
  refunds: number;
  netProfit: number;
} | null {
  const payload = unwrapPayload(raw);
  const revenue = (() => {
    for (const key of [
      "total_sales",
      "net_revenue",
      "revenue",
      "total_revenue",
      "order_total_gross_amount",
    ] as const) {
      const n = readMoneyScalar(payload, key);
      if (Number.isFinite(n)) {
        return n;
      }
    }
    return Number.NaN;
  })();
  if (!Number.isFinite(revenue)) {
    return null;
  }
  const taxes = (() => {
    const fromTotalTaxes = readMoneyScalar(payload, "total_taxes");
    if (Number.isFinite(fromTotalTaxes)) {
      return fromTotalTaxes;
    }
    const fromTaxes = readMoneyScalar(payload, "taxes");
    if (Number.isFinite(fromTaxes)) {
      return fromTaxes;
    }
    return readNumber(payload.tax, 0);
  })();
  const expenses = (() => {
    const fromExpenses = readMoneyScalar(payload, "expenses");
    if (Number.isFinite(fromExpenses)) {
      return fromExpenses;
    }
    return readNumber(payload.operating_expenses, 0);
  })();
  const refunds = (() => {
    const nested = readMoneyScalar(payload, "refunds");
    if (Number.isFinite(nested)) {
      return nested;
    }
    return readNumber(payload.refunds, 0);
  })();
  const cogs = readNumber(payload.cogs ?? payload.cost_of_goods, revenue * 0.41);
  const grossProfit = readNumber(payload.gross_profit, revenue - cogs);
  const netProfit = (() => {
    const fromNetProfit = readMoneyScalar(payload, "net_profit");
    if (Number.isFinite(fromNetProfit)) {
      return fromNetProfit;
    }
    const fromNetIncome = readMoneyScalar(payload, "net_income");
    if (Number.isFinite(fromNetIncome)) {
      return fromNetIncome;
    }
    return grossProfit - taxes - expenses - refunds;
  })();
  return { revenue, cogs, grossProfit, taxes, expenses, refunds, netProfit };
}

/** Same numeric shape as `getFinancialSummary` mock helper — always returns numbers (zeros if missing). */
export function normalizeFinancialsSummaryToFinancialBlock(raw: unknown): {
  revenue: number;
  revenueChangePercent?: number | null;
  cogs: number;
  grossProfit: number;
  taxes: number;
  taxesChangePercent?: number | null;
  expenses: number;
  refunds: number;
  refundsChangePercent?: number | null;
  netProfit: number;
  netProfitChangePercent?: number | null;
  comparisonLabel?: string;
} {
  const payload = unwrapPayload(raw);
  const totalSalesRaw = payload.total_sales ?? payload.net_revenue;
  const refundsRaw = payload.refunds;
  const hasNestedFinancialsSummary =
    (isRecord(totalSalesRaw) && "value" in totalSalesRaw) ||
    (isRecord(refundsRaw) && "value" in refundsRaw);

  if (hasNestedFinancialsSummary) {
    const revSeries = readMetricSeriesFromKeys(payload, ["total_sales", "net_revenue"]);
    const revenue = revSeries
      ? revSeries.value
      : (readMoneyScalar(payload, "total_sales") ?? readMoneyScalar(payload, "net_revenue"));
    const rev = Number.isFinite(revenue) ? revenue : 0;

    const refundsSeries = readMetricSeries(payload.refunds);
    const refundsVal = refundsSeries
      ? refundsSeries.value
      : (() => {
        const nested = readMoneyScalar(payload, "refunds");
        return Number.isFinite(nested) ? nested : readNumber(payload.refunds, 0);
      })();

    const taxesSeries = readMetricSeries(payload.total_taxes);
    const taxes = taxesSeries
      ? taxesSeries.value
      : (() => {
        const s = readMoneyScalar(payload, "total_taxes");
        if (Number.isFinite(s)) { return s; }
        const s2 = readMoneyScalar(payload, "taxes");
        if (Number.isFinite(s2)) { return s2; }
        return readNumber(payload.tax, 0);
      })();

    const expensesSeries = readMetricSeries(payload.expenses);
    const expenses = expensesSeries
      ? expensesSeries.value
      : (() => {
        const s = readMoneyScalar(payload, "expenses");
        return Number.isFinite(s) ? s : readNumber(payload.operating_expenses, 0);
      })();

    const netProfitSeries = readMetricSeries(payload.net_profit);
    const netProfit = netProfitSeries
      ? netProfitSeries.value
      : (() => {
        const s = readMoneyScalar(payload, "net_profit");
        if (Number.isFinite(s)) { return s; }
        const s2 = readMoneyScalar(payload, "net_income");
        return Number.isFinite(s2) ? s2 : 0;
      })();

    const cogs = readNumber(payload.cogs ?? payload.cost_of_goods, 0);
    const grossProfit = readNumber(
      payload.gross_profit ?? payload.gross_profit_line,
      Math.max(0, rev - cogs),
    );

    return {
      revenue: rev,
      revenueChangePercent: revSeries?.pct,
      cogs,
      grossProfit,
      taxes,
      taxesChangePercent: taxesSeries?.pct,
      expenses,
      refunds: Number.isFinite(refundsVal) ? refundsVal : 0,
      refundsChangePercent: refundsSeries?.pct,
      netProfit,
      netProfitChangePercent: netProfitSeries?.pct,
      comparisonLabel: comparisonLabelFromOverviewRoot(payload),
    };
  }

  const parsed = normalizeSalesSummaryToFinancial(raw);
  if (parsed) {
    return parsed;
  }
  const revenue = readNumber(payload.revenue ?? payload.total_revenue, 0);
  const taxes = readNumber(payload.total_taxes ?? payload.taxes ?? payload.tax, 0);
  const expenses = readNumber(payload.expenses ?? payload.operating_expenses, 0);
  const refunds = (() => {
    const nested = readMoneyScalar(payload, "refunds");
    if (Number.isFinite(nested)) {
      return nested;
    }
    return readNumber(payload.refunds, 0);
  })();
  const cogs = readNumber(payload.cogs ?? payload.cost_of_goods, 0);
  const grossProfit = readNumber(payload.gross_profit, Math.max(0, revenue - cogs));
  const netProfit = readNumber(
    payload.net_profit ?? payload.net_income,
    grossProfit - taxes - expenses - refunds,
  );
  return { revenue, cogs, grossProfit, taxes, expenses, refunds, netProfit };
}

export function normalizeFinancialsMonthlyTrendResponse(raw: unknown): MonthlyPoint[] {
  const payload = unwrapPayload(raw);
  const series = asArray(
    payload.rows ??
    payload.monthly_trend ??
    payload.trend ??
    payload.series ??
    payload.months ??
    payload.items,
  );
  if (!series.length) {
    return normalizeSalesSummaryToMonthly(raw);
  }
  return series.map((row, index) => {
    if (!isRecord(row)) {
      return { month: String(index), revenue: 0, expenses: 0, profit: 0 };
    }
    return {
      month: readString(
        row.month ?? row.month_label ?? row.label ?? row.period ?? row.name,
        String(index),
      ),
      revenue: readNumber(
        row.total_sales ?? row.revenue ?? row.net_revenue ?? row.total_revenue ?? row.amount,
        0,
      ),
      expenses: readNumber(row.expenses ?? row.operating_expenses ?? row.costs, 0),
      profit: readNumber(row.profit ?? row.net_profit, 0),
    };
  });
}

export type FinancialPnlBarRow = {
  label: string;
  value: number;
  ratio: number;
};

/**
 * Maps `financials/pnl` into waterfall rows. Falls back to deriving ratios from summary-like payload.
 */
export function normalizeFinancialsPnlBars(
  raw: unknown,
  summaryFallback: { revenue: number; cogs: number; grossProfit: number; taxes: number; expenses: number; refunds: number; netProfit: number },
): FinancialPnlBarRow[] {
  const payload = unwrapPayload(raw);
  const rows = asArray(payload.rows ?? payload.line_items ?? payload.breakdown ?? payload.items);
  const revenueBase = Math.max(
    summaryFallback.revenue,
    readNumber(payload.gross_revenue, 0),
    1e-9,
  );

  if (!rows.length && ("gross_revenue" in payload || "gross_profit_line" in payload || "net_profit" in payload)) {
    const gr = readNumber(payload.gross_revenue, 0);
    const cogs = readNumber(payload.cogs, 0);
    const gpl = readNumber(payload.gross_profit_line ?? payload.gross_profit, gr - cogs);
    const opex = readNumber(payload.operating_expenses, 0);
    const tax = readNumber(payload.taxes, 0);
    const ref = readNumber(payload.refunds, 0);
    const net = readNumber(payload.net_profit, 0);
    const base = Math.max(gr, 1e-9);
    return [
      { label: "Gross Revenue", value: gr, ratio: 100 },
      { label: "COGS", value: -cogs, ratio: (Math.abs(cogs) / base) * 100 },
      { label: "Gross Profit", value: gpl, ratio: (Math.abs(gpl) / base) * 100 },
      { label: "Operating Expenses", value: -opex, ratio: (Math.abs(opex) / base) * 100 },
      { label: "Taxes", value: -tax, ratio: (Math.abs(tax) / base) * 100 },
      { label: "Refunds", value: -ref, ratio: (Math.abs(ref) / base) * 100 },
      { label: "Net Profit", value: net, ratio: (Math.abs(net) / base) * 100 },
    ];
  }

  if (rows.length) {
    return rows
      .map((row, index) => {
        if (!isRecord(row)) {
          return null;
        }
        const label = readString(row.label ?? row.name ?? row.title, `Line ${index + 1}`);
        const value = readNumber(row.value ?? row.amount ?? row.total, 0);
        const ratio = readNumber(row.ratio ?? row.pct, (Math.abs(value) / revenueBase) * 100);
        return { label, value, ratio };
      })
      .filter(Boolean) as FinancialPnlBarRow[];
  }

  const s = summaryFallback;
  return [
    { label: "Gross Revenue", value: s.revenue, ratio: 100 },
    { label: "Cost of Goods Sold", value: -s.cogs, ratio: (s.cogs / revenueBase) * 100 },
    { label: "Gross Profit", value: s.grossProfit, ratio: (s.grossProfit / revenueBase) * 100 },
    { label: "Operating Expenses", value: -s.expenses, ratio: (s.expenses / revenueBase) * 100 },
    { label: "Taxes", value: -s.taxes, ratio: (s.taxes / revenueBase) * 100 },
    { label: "Refunds", value: -s.refunds, ratio: (s.refunds / revenueBase) * 100 },
    { label: "Net Profit", value: s.netProfit, ratio: (s.netProfit / revenueBase) * 100 },
  ];
}

export type CustomersSummaryView = {
  customersInPeriod: number;
  customersInPeriodChangePercent?: number | null;
  /** Pre-formatted count from the API, preferred over formatting `customersInPeriod` locally. */
  customersInPeriodFormattedValue?: string;
  newCustomers: number;
  newCustomersChangePercent?: number | null;
  returningCustomers: number;
  totalCustomersOrdered: number;
  totalCustomersOrderedChangePercent?: number | null;
  /** Pre-formatted count from the API, preferred over formatting `totalCustomersOrdered` locally. */
  totalCustomersOrderedFormattedValue?: string;
  avgCltv: number;
  avgOrderValue: number;
  avgOrderValueChangePercent?: number | null;
  /** Pre-formatted amount from the API (e.g. "USD 3,147.74"), preferred over formatting `avgOrderValue` locally. */
  avgOrderValueFormattedValue?: string;
  avgBasketSize: number;
  /** Pre-formatted decimal from the API (2 decimals), preferred over formatting `avgBasketSize` locally. */
  avgBasketSizeFormattedValue?: string;
  comparisonLabel?: string;
  cltvBuckets: BucketPoint[];
  segmentation: DistributionPoint[];
};

export function normalizeCustomersSummaryResponse(raw: unknown): CustomersSummaryView {
  const payload = unwrapPayload(raw);
  const customersInPeriodSeries = readMetricSeries(
    payload.customers_in_period ??
      payload.total_customers ??
      payload.customer_count ??
      payload.total ??
      payload.count,
  );
  const customersInPeriod = customersInPeriodSeries
    ? customersInPeriodSeries.value
    : readNumber(
        payload.customers_in_period ??
          payload.total_customers ??
          payload.customer_count ??
          payload.total ??
          payload.count,
        0,
      );

  const newCustomersSeries = readMetricSeries(
    payload.new_customers ?? payload.new ?? payload.acquisitions,
  );
  const newCustomers = newCustomersSeries
    ? newCustomersSeries.value
    : readNumber(payload.new_customers ?? payload.new ?? payload.acquisitions, 0);

  const returningCustomers = readNumber(
    payload.returning_customers ?? payload.returning ?? payload.repeat,
    Math.max(0, customersInPeriod - newCustomers),
  );
  const avgCltv = readNumber(payload.avg_cltv ?? payload.average_cltv ?? payload.cltv_avg, 0);

  const avgOrderValueSeries = readMetricSeries(
    payload.avg_order_value ?? payload.average_order_value ?? payload.aov,
  );
  const avgOrderValue = avgOrderValueSeries
    ? avgOrderValueSeries.value
    : readNumber(payload.avg_order_value ?? payload.average_order_value ?? payload.aov, 0);

  const bucketSource = asArray(
    payload.cltv_distribution ?? payload.cltv_buckets ?? payload.cltv_breakdown,
  );
  const cltvBuckets: BucketPoint[] = bucketSource.map((row, index) => {
    if (!isRecord(row)) {
      return { label: String(index), value: 0 };
    }
    return {
      label: readString(row.label ?? row.bucket ?? row.range, `Bucket ${index + 1}`),
      value: readNumber(row.value ?? row.count ?? row.customers, 0),
    };
  });

  const nvSource = asArray(payload.new_vs_returning);
  const segOnly = asArray(payload.segmentation ?? payload.customer_segmentation);
  let segmentation: DistributionPoint[] = [];
  if (nvSource.length) {
    segmentation = nvSource.map((row, index) => {
      if (!isRecord(row)) {
        return { label: String(index), value: 0 };
      }
      const formattedValue = readString(
        row.customersFormattedValue ?? row.valueFormattedValue,
        "",
      ).trim() || undefined;
      return {
        label: normalizeCustomerSegmentLabel(
          row.customer_type ?? row.label ?? row.segment ?? row.type,
          "—",
        ),
        value: readNumber(row.customers ?? row.value ?? row.count, 0),
        ...(formattedValue ? { formattedValue } : {}),
      };
    });
  } else if (segOnly.length) {
    segmentation = segOnly.map((row, index) => {
      if (!isRecord(row)) {
        return { label: String(index), value: 0 };
      }
      return {
        label: normalizeCustomerSegmentLabel(row.label ?? row.segment ?? row.customer_type ?? row.type),
        value: readNumber(row.value ?? row.count ?? row.customers, 0),
      };
    });
  } else if (customersInPeriod > 0 || newCustomers > 0) {
    segmentation = [
      { label: "New", value: newCustomers },
      { label: "Returning", value: returningCustomers },
    ];
  }

  const totalCustomersOrderedSeries = readMetricSeries(
    payload.total_customers_ordered ?? payload.customers_ordered,
  );
  const totalCustomersOrdered = totalCustomersOrderedSeries
    ? totalCustomersOrderedSeries.value
    : readNumber(payload.total_customers_ordered ?? payload.customers_ordered, 0);

  const avgBasketSize = readNumber(payload.avg_basket_size, 0);
  const avgBasketSizeFormattedValue = readString(payload.avg_basket_sizeFormattedValue, "").trim() || undefined;

  return {
    customersInPeriod,
    customersInPeriodChangePercent: customersInPeriodSeries?.pct,
    customersInPeriodFormattedValue: customersInPeriodSeries?.formattedValue,
    newCustomers,
    newCustomersChangePercent: newCustomersSeries?.pct,
    returningCustomers,
    totalCustomersOrdered,
    totalCustomersOrderedChangePercent: totalCustomersOrderedSeries?.pct,
    totalCustomersOrderedFormattedValue: totalCustomersOrderedSeries?.formattedValue,
    avgCltv,
    avgOrderValue,
    avgOrderValueChangePercent: avgOrderValueSeries?.pct,
    avgOrderValueFormattedValue: avgOrderValueSeries?.formattedValue,
    avgBasketSize,
    avgBasketSizeFormattedValue,
    comparisonLabel: comparisonLabelFromOverviewRoot(payload),
    cltvBuckets,
    segmentation,
  };
}

function normalizeCustomerAccountStatus(raw: string): SalesCustomer["status"] {
  const s = raw.toLowerCase().trim();
  if (s === "enabled" || s === "disabled" || s === "invited") {
    return s;
  }
  return s || "Not Available";
}

export function normalizeCustomersAllResponse(raw: unknown): {
  customers: SalesCustomer[];
  total: number;
  totalPages: number;
  page: number;
  pageSize: number;
} {
  const root = isRecord(raw) ? raw : {};
  const payload = unwrapPayload(raw);
  const meta = isRecord(payload.meta)
    ? payload.meta
    : isRecord(root.meta)
      ? root.meta
      : null;
  const rows = asArray(payload.items ?? payload.customers ?? payload.results ?? payload.rows);

  const customers = rows.map((row, index) => {
    if (!isRecord(row)) {
      return {
        id: `cust-${index}`,
        name: "Not Available",
        email: "Not Available",
        phone: "Not Available",
        segments: [],
        totalOrders: 0,
        totalSpent: 0,
        avgOrderValue: 0,
        recentOrderDate: "Not Available",
        signUpDate: "Not Available",
        status: "Not Available",
        cltv: 0,
      };
    }
    const id = readString(row.id ?? row.customer_id ?? `cust-${index}`, `cust-${index}`);
    const name = readString(row.name ?? row.customer_name ?? row.display_name, "").trim() || "Not Available";
    const email = readString(row.email ?? row.customer_email, "").trim() || "Not Available";
    const phone = readString(
      row.phone ?? row.mobile ?? row.phone_number ?? row.customer_phone,
      "",
    ).trim() || "Not Available";
    const segments = readStringList(row.segments ?? row.segment ?? row.customer_segments);
    const totalOrders = readNumber(row.orders_count ?? row.total_orders ?? row.orders, 0);
    const totalOrdersFormattedValue = readString(row.total_ordersFormattedValue, "").trim() || undefined;
    const totalSpent = readNumber(
      row.total_spend ?? row.total_spent ?? row.lifetime_value ?? row.revenue,
      0,
    );
    const totalSpentFormattedValue = readString(
      row.totalSpendFormattedValue ?? row.total_spendFormattedValue ?? row.totalSpentFormattedValue,
      "",
    ).trim() || undefined;
    const avgOrderValue = readNumber(
      row.avg_order_value ?? row.aov,
      totalOrders ? totalSpent / totalOrders : 0,
    );
    const recentOrderDate = readString(
      row.recent_order_date ??
        row.last_order_date ??
        row.last_order ??
        row.last_purchase,
      "Not Available",
    );
    const recentOrderDateDisplay = formatDateLikeDisplay(recentOrderDate);
    const recentOrderDateFormattedValue = readString(row.recent_order_dateFormattedValue, "").trim() || undefined;
    const signUpDate = readString(
      row.sign_up_date ?? row.created_at_date ?? row.created_at,
      "Not Available",
    );
    const signUpDateDisplay = formatDateLikeDisplay(signUpDate);
    const signUpDateFormattedValue = readString(row.sign_up_dateFormattedValue, "").trim() || undefined;
    const cltv = readNumber(row.cltv ?? row.predicted_cltv ?? row.lifetime_value, totalSpent);
    const statusRaw = readString(
      row.status ?? row.customer_type ?? row.type ?? row.segment,
      "",
    );

    return {
      id,
      name,
      email,
      phone,
      segments,
      totalOrders,
      totalOrdersFormattedValue,
      totalSpent,
      totalSpentFormattedValue,
      avgOrderValue,
      recentOrderDate: recentOrderDateDisplay,
      recentOrderDateFormattedValue,
      signUpDate: signUpDateDisplay,
      signUpDateFormattedValue,
      status: normalizeCustomerAccountStatus(statusRaw),
      cltv,
    };
  });

  const total = readNumber(
    meta?.total_items ??
      meta?.total ??
      meta?.total_count ??
      payload.total ??
      payload.total_count ??
      payload.count ??
      root.total ??
      customers.length,
    customers.length,
  );
  const page = readNumber(meta?.page ?? payload.page ?? root.page, 1);
  const pageSize = readNumber(
    meta?.page_size ?? payload.page_size ?? payload.per_page ?? root.page_size,
    10,
  );
  const totalPages = readNumber(
    meta?.total_pages ?? payload.total_pages ?? root.total_pages,
    Math.max(1, Math.ceil(total / Math.max(1, pageSize))),
  );

  return { customers, total, totalPages, page, pageSize };
}

export function normalizeInventoryProductsDetailResponse(raw: unknown): {
  products: SalesProduct[];
  total: number;
  totalPages: number;
  page: number;
  pageSize: number;
} {
  const root = isRecord(raw) ? raw : {};
  const payload = unwrapPayload(raw);
  const meta = isRecord(payload.meta)
    ? payload.meta
    : isRecord(root.meta)
      ? root.meta
      : null;
  const rows = asArray(payload.items ?? payload.products ?? payload.results ?? payload.rows);
  const products = rows.map((row, index) => {
    if (!isRecord(row)) {
      return {
        id: `prod-${index}`,
        name: "—",
        sku: "",
        category: "",
        price: 0,
        cost: 0,
        stock: 0,
        reorderPoint: 0,
        status: "in_stock" as SalesProduct["status"],
        statusUnavailable: true,
      };
    }
    const id = readString(row.id ?? row.product_id ?? row.variant_id ?? `prod-${index}`, `prod-${index}`);
    const name = readString(
      row.product ?? row.title ?? row.name ?? row.product_name,
      "—",
    );
    const sku = readString(row.sku ?? row.variant_sku, "");
    const category = readString(row.category ?? row.product_type, "");
    const price = readNumber(row.avg_price ?? row.price ?? row.unit_price, 0);
    const priceFormattedValue = readString(
      row.avgPriceFormattedValue ?? row.priceFormattedValue,
      "",
    ).trim() || undefined;
    const cost = readNumber(row.cost ?? row.unit_cost, 0);
    const stockRaw = row.stock ?? row.inventory_quantity ?? row.quantity_on_hand;
    const stockUnavailable = isUnavailableDisplayValue(stockRaw);
    const stock = stockUnavailable ? 0 : readNumber(stockRaw, 0);
    const stockFormattedValue = readString(row.stockFormattedValue, "").trim() || undefined;
    const reorderPoint = readNumber(row.reorder_point ?? row.reorder_level, 0);
    const statusRaw = row.status ?? row.inventory_status;
    const normalizedStatus = normalizeInventoryProductStatus(statusRaw);
    const imageUrl = readString(
      row.product_image_url ??
        row.image_url ??
        row.imageUrl ??
        row.product_image ??
        row.image ??
        row.thumbnail_url ??
        row.thumbnail,
      "",
    );
    return {
      id,
      name,
      sku,
      category,
      price,
      ...(priceFormattedValue ? { priceFormattedValue } : {}),
      cost,
      stock,
      ...(stockFormattedValue ? { stockFormattedValue } : {}),
      ...(stockUnavailable ? { stockUnavailable } : {}),
      reorderPoint,
      status: normalizedStatus ?? "in_stock",
      ...(normalizedStatus ? {} : { statusUnavailable: true }),
      ...(imageUrl ? { imageUrl } : {}),
    };
  });

  const total = readNumber(
    meta?.total_items ??
    meta?.total ??
    meta?.total_count ??
    payload.total ??
    payload.total_count ??
    payload.count ??
    root.total ??
    products.length,
    products.length,
  );
  const page = readNumber(meta?.page ?? payload.page ?? root.page, 1);
  const pageSize = readNumber(
    meta?.page_size ?? payload.page_size ?? payload.per_page ?? root.page_size,
    10,
  );
  const totalPages = readNumber(
    meta?.total_pages ?? meta?.pages ?? null,
    Math.ceil(total / Math.max(1, pageSize)),
  );

  return { products, total, totalPages, page, pageSize };
}
